/**
 * This is a component wrapper around the froala editor
 *
 * The implementation is intentionally pedantic in that is makes excessive use of property accessors and mutators and has extensive logging
 * that may be enabled for debugging. Try using [isConsoleLog]="true" in the markup.
 */
import { Component, EventEmitter, Input, OnInit, Output, Inject, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { APP_CONFIG, AppConfig } from '../../../app-config.module';


@Component({
  selector: 'app-rich-content',
  templateUrl: './rich-content.component.html',
  styleUrls: ['./rich-content.component.scss']
})
export class RichContentComponent implements OnInit {
  private static readonly MAX_CONTENT_LENGTH = 300;
  private static readonly PLACE_HOLDER_TEXT = 'Enter Text Here';

  // buttons in the order from the documentation
  // https://www.froala.com/wysiwyg-editor/docs/options#toolbarButtons
  private static readonly TOOLBAR_BUTTONS = ['bold', 'underline', 'italic', 'color', 'formatUL'];
  private static readonly PLUGINS_ENABLED = ['align', 'charCounter', 'colors', 'entities', 'lineBreaker', 'lists', 'save', 'wordPaste'];
  private static readonly AUTO_SAVE_TIME = 4000; // 4 seconds (5 seconds including the debounce in froala itself)

  private _editorTitle = '';
  private _placeholderText = RichContentComponent.PLACE_HOLDER_TEXT;
  private _maxContentLength = RichContentComponent.MAX_CONTENT_LENGTH;
  private _isReadOnly = false;
  private _isConsoleLog = false;
  private _content = '';

  @Output() contentUpdate = new EventEmitter<string>();

  @Input() heightOverride = -1;
  @Input() autoFocus = false;
  @Input() forceEdit = false;
  @Input() updateTimeOverride = 0;

  private content$: Subject<string>;
  private editor;

  /**
   * This field is only used for debugging purposes. It allows filtering the dev tools console with a unique string so that only log
   * messages containing the value generated at runtime. This may seem extreme but, history has shown that debugging the froala component
   * is tricky to debug.
   */
  private debugInstanceId: string;

  constructor(@Inject(APP_CONFIG) private appConfig: AppConfig, private changeDetectorRef: ChangeDetectorRef) { }

  ngOnInit() {
    // PLEASE KEEP, UNCOMMENTING IS CONVENIENT WHEN DEBUGGING
    this.consoleLog('ngOnInit');
  }

  get editorTitle(): string {
    return this._editorTitle;
  }

  @Input() set editorTitle(editorTitle: string) {
    const isChanged = this._editorTitle !== editorTitle;
    this.consoleLog(`set editorTitle=${this._editorTitle}->${editorTitle}, changed: ${isChanged}`);
    if (isChanged) {
      this._editorTitle = editorTitle;
    }
  }

  get placeholderText(): string {
    return this._placeholderText;
  }

  @Input() set placeholderText(placeholderText: string) {
    const isChanged = this._placeholderText !== placeholderText;
    this.consoleLog(`set placeholderText=${this._placeholderText}->${placeholderText}, changed: ${isChanged}`);
    if (isChanged) {
      this._placeholderText = placeholderText;
      if (!this.isReadOnly) {
        this.setEditorPlaceHolder(this.placeholderText);
      }
    }
  }

  @Input() get maxContentLength() {
    return this._maxContentLength;
  }

  set maxContentLength(maxContentLength) {
    const isChanged = this.maxContentLength !== maxContentLength;
    this.consoleLog(`set maxContentLength '${this.maxContentLength}'->'${maxContentLength}' changed ${isChanged}`);
    if (isChanged) {
      this._maxContentLength = maxContentLength;
    }
  }

  get isReadOnly(): boolean {
    this.consoleLog(`get isReadOnly=${this._isReadOnly}`);
    return this._isReadOnly;
  }

  @Input() set isReadOnly(isReadOnly: boolean) {
    const isChanged = this.isReadOnly !== isReadOnly;
    this.consoleLog(`set isReadOnly=${this.isReadOnly}->${isReadOnly}, changed: ${isChanged}`);
    if (isChanged) {
      this._isReadOnly = isReadOnly;
      let placeHolder;
      if (this.isReadOnly) {
        this.isEdit = false;
        placeHolder = '';
      } else {
        placeHolder = this.placeholderText;
      }
      this.setEditorPlaceHolder(placeHolder);
      this.detectChanges();
    }
  }

  get isConsoleLog(): boolean {
    return this._isConsoleLog;
  }

  @Input() set isConsoleLog(isConsoleLog: boolean) {
    const isChanged = this._isConsoleLog !== isConsoleLog;
    this.consoleLog(`set isConsoleLog=${this._isConsoleLog}->${isConsoleLog}, changed: ${isChanged}`);
    if (isChanged) {
      this._isConsoleLog = isConsoleLog;
      if (isConsoleLog) {
        // transitioned to logging need to log again so it is seen
        this.consoleLog(`set isConsoleLog (post)=${this._isConsoleLog}->${isConsoleLog}, changed: ${isChanged}`);
      }
    }
  }

  get content(): string {
    return this._content;
  }

  @Input() set content(content: string) {
    this.consoleLog(`Content is being set: ${content}`);
    const normalizedContent = content || '';
    const isChanged = this.content !== normalizedContent;
    this.consoleLog(`set content '${this.content}'->'${normalizedContent}' ignored ${!isChanged}`);
    if (isChanged) {
      this._content = normalizedContent;
      this.synchronizeEditorContent();
    }
  }

  get contentLengthRemaining() {
    return this.maxContentLength - this.getEditorContentLength();
  }

  // ==========================================================================
  // direct interaction and support for the froala editor

  get options() {
    const options = {
      key: this.appConfig.froalaLicenseKey,
      theme: 'strategies',
      charCounterMax: this._maxContentLength,
      placeholderText: this.placeholderText,
      toolbarInline: false,
      toolbarButtons: RichContentComponent.TOOLBAR_BUTTONS,
      pastePlain: true,
      pluginsEnabled: RichContentComponent.PLUGINS_ENABLED,
      events: {
        'froalaEditor.initialized': (event, editor) => this.onFroalaEditorInitialized(event, editor),
        'froalaEditor.focus': (event, editor) => this.onFroalaEditorFocus(event, editor),
        'froalaEditor.blur': (event, editor) => this.onFroalaEditorBlur(event, editor)
      }
    };
    if (this.heightOverride >= 0) {
      options['height'] = this.heightOverride;
    }
    this.consoleLog('get options');
    return options;
  }

  get isEdit(): boolean {
    this.consoleLog(`get isEdit=${!!this.editor},${!!this.editor && !this.editor.edit.isDisabled()}`);
    return this.editor && !this.editor.edit.isDisabled();
  }

  set isEdit(isEdit: boolean) {
    const isChanged = this.isEdit !== isEdit;
    this.consoleLog(`set isEdit=${this.isEdit}->${isEdit}, changed: ${isChanged}`);
    if (isChanged) { // is the edit flag actually changing
      if (this.editor) {
        if (isEdit && !this.isReadOnly) {
          this.editOn(); // transitioning to edit
        } else {
          this.editOff(); // transitioning to view
        }
      }
      this.detectChanges();
    }
  }

  private setEditorPlaceHolder(placeHolderText: string) {
    if (this.editor) {
      // inputs may change after froala init. Need to set placeholder text options and refresh
      this.editor.opts.placeholderText = placeHolderText;
      this.editor.placeholder.refresh();
    }
  }

  private get editorContent() {
    return (this.editor && this.editor.html) ? this.editor.html.get() : undefined;
  }

  private set editorContent(content) {
    if (this.editor) {
      if (!this.isEditorContentSynchronized) {
        const disabled = this.editor.edit.isDisabled();
        // temporarily set edit on so the changes will take
        if (disabled) {
          this.editor.edit.on();
        }
        if (this.editor && this.editor.html) {
          this.editor.html.set(content);
        }
        if (disabled) {
          this.editor.edit.off();
        }
      }
    }
  }

  private getEditorContentLength(defaultValue = 0) {
    return (this.editor && this.editor.charCounter) ? this.editor.charCounter.count() : defaultValue;
  }

  // ==========================================================================
  // bridge the component content and the froala editor content

  private get isEditorContentSynchronized() {
    return this.editorContent === this.content;
  }

  private synchronizeEditorContent() {
    if (this.editor) {
      this.editorContent = this.content;
      this.contentSubjectNext(this.content);
    }
  }

  private editOn() {
    this.editor.toolbar.show();
    this.editor.edit.on();
    this.initializeContent$();
  }

  private editOff() {
    this.editor.toolbar.hide();
    this.editor.edit.off();
    this.clearContentSubject$();
  }

  // start the content subscription
  private initializeContent$() {
    this.consoleLog(`Initialize Content`);
    this.content$ = new Subject();
    this.content$.pipe(
      debounceTime(this.updateTimeOverride !== 0 ? this.updateTimeOverride : RichContentComponent.AUTO_SAVE_TIME),
      distinctUntilChanged()
    ).subscribe(() => this.contentUpdate.emit(this.content));

    this.editor.el.click();
    this.editor.el.focus();
  }

  // end the content subscription
  private clearContentSubject$() {
    if (this.content$) {
      this.content$.complete();
      this.content$ = undefined;
    }
  }

  private contentSubjectNext(content) {
    if (this.content$) {
      this.content$.next(content);
    }
  }

  // ==========================================================================
  // The following functions are only bound through the froala configuration

  private onFroalaEditorInitialized(event, editor) {
    this.consoleLog('fr-initialized');
    this.editor = editor; // keep track of the froala editor to check if it is disabled
    this.isEdit = false;
    this.editor.el.addEventListener('click', () => this.onFroalaClickSetFocus()); // add a native click listener to the froala editor
    // TODO remove click listener on destroy
    this.isEdit = this.autoFocus;
  }

  private onFroalaEditorFocus(event, editor) {
    this.consoleLog('fr-focus');
    this.isEdit = true;
  }

  private onFroalaEditorBlur(event, editor) {
    this.consoleLog(`fr-blur ${this.isEditorContentSynchronized} ${this.editorContent} ${this.content}`);
    this.isEdit = false;
    if (!this.isEditorContentSynchronized) {
      this.content = this.editorContent;
      this.contentUpdate.emit(this.content);
    }
    if (this.forceEdit) {
      this.editor.toolbar.show();
    }
  }

  // ==========================================================================
  // bound directly to the froala editor retained in froala initialization
  private onFroalaClickSetFocus() {
    this.consoleLog('onFroalaClickSetFocus');
    if (!this.isEdit) { // ignore the click if already editing
      this.isEdit = true;
    }
    this.editor.el.click();
    this.editor.el.focus();
  }

  private detectChanges() {
    this.consoleLog('detectChanges start');
    this.changeDetectorRef.detectChanges();
    this.consoleLog('detectChanges end');
  }

  private consoleLog(message?: any, ...optionalParams: any[]) {
    // PLEASE KEEP, CONVENIENT WHEN DEBUGGING
    if (this.isConsoleLog) {
      if (!this.debugInstanceId) {
        // lazily create the debugging instance identifier
        this.debugInstanceId = Math.floor(Math.random() * 100000000).toString(8).padStart(8, '0');
      }
      console.log(`RichContent:'${this.debugInstanceId}':${message}`, ...optionalParams);
    }
  }
}
